import type { MouseEvent } from 'react';
import type { AssetKind } from '@shared/types';
import { Icon } from '@renderer/shared/components/icons';

function getAssetKindIcon(kind: AssetKind): Parameters<typeof Icon>[0]['name'] {
  switch (kind) {
    case 'image':
      return 'images';
    case 'pdf':
    case 'document':
      return 'document';
    case 'logo':
    case 'icon':
      return 'icon';
    default:
      return 'document';
  }
}

function isInteractiveTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  return Boolean(target.closest('a,button,input,select,textarea,[role="button"]'));
}

export function ResourceCard({
  id,
  name,
  kind,
  kindLabel,
  metaLabel,
  previewUrl,
  selected,
  onSelect,
  onOpenDetails
}: {
  id: string;
  name: string;
  kind: AssetKind;
  kindLabel: string;
  metaLabel: string;
  previewUrl: string | null;
  selected: boolean;
  onSelect: (id: string) => void;
  onOpenDetails: (id: string) => void;
}): JSX.Element {
  const placeholderTone =
    kind === 'pdf' ? 'warm' : kind === 'icon' || kind === 'logo' ? 'blue' : kind === 'image' ? 'cool' : 'neutral';

  return (
    <article
      className={['av-resource-card', selected ? 'is-selected' : ''].filter(Boolean).join(' ')}
    >
      <button
        className="av-resource-card__preview"
        type="button"
        aria-pressed={selected}
        aria-label={name}
        onClick={(event) => {
          event.stopPropagation();
          onSelect(id);
        }}
      >
        {previewUrl ? (
          <img alt="" className="av-resource-card__preview-img" src={previewUrl} />
        ) : (
          <div
            className={[
              'av-resource-card__preview-fallback',
              placeholderTone === 'warm' ? 'is-placeholder-warm' : '',
              placeholderTone === 'cool' ? 'is-placeholder-cool' : '',
              placeholderTone === 'blue' ? 'is-placeholder-blue' : ''
            ]
              .filter(Boolean)
              .join(' ')}
            aria-hidden
          >
            <Icon name={getAssetKindIcon(kind)} />
          </div>
        )}

        {selected ? (
          <div className="av-resource-card__selected-indicator" aria-hidden>
            <Icon name="check" />
          </div>
        ) : null}
      </button>

      <div
        className="av-resource-card__body"
        role="button"
        tabIndex={0}
        onClick={(event: MouseEvent<HTMLElement>) => {
          if (isInteractiveTarget(event.target)) {
            return;
          }

          onOpenDetails(id);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onOpenDetails(id);
          }
        }}
      >
        <div className="av-resource-card__title-row">
          <button className="av-resource-card__title" type="button" onClick={() => onOpenDetails(id)}>
            {name}
          </button>
          <span className="av-type-pill">{kindLabel}</span>
        </div>
        <div className="av-resource-card__meta">
          <Icon name="timer" />
          <span>{metaLabel}</span>
        </div>
      </div>
    </article>
  );
}
